import React, { FC, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useLocation, useNavigate } from 'react-router-dom';
import Divider from 'components/divider/Divider';

import Lottie from 'lottie-react';
import { NavCloseIcon, OrangeInfoIcon, RedirectIcon, WhiteRightArrowSmall } from 'assets/icons';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectCurrentTeam,
  selectCurrentTeamInvoicePermissions,
  selectPaymentSystemConfig,
  selectTeamBankDetails,
  showNotifier
} from 'containers/app/appSlice';
import { useLazyGetUpcomingPayoutsSummaryQuery } from 'containers/payouts/api';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { CTA_TYPES, PaymentSystem, PayoutRootPage } from 'containers/payouts/constants';
import { CustomButton, If, NoPermissions, SidePanel } from 'components';
import { NotifierTypes } from 'constants/notifierConstants';
import { checkPermissions } from 'containers/payouts/utils';
import PayoutDistributionTimeline from 'containers/payouts/payouts-balance/components/PayoutDistributionTimeline';
import { PayoutsCleverTapEvents } from 'containers/payouts/events';
import { logAnalyticEvent } from 'utils/analytics';
import { openScanpayFAQInNewTab } from 'utils/urlUtils';
import { openInNewTab } from 'utils/commonUtils';
import DateUtils from 'utils/dateUtils';

const UpComingPayoutDetailsPanel: FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { handleSidePanelClose } = useOutletContext<any>();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentTeamBank = useAppSelector(selectTeamBankDetails);
  const navigate = useNavigate();
  const [getUpcomingPayoutsSummaryQuery, getUpcomingPayoutsSummaryState] = useLazyGetUpcomingPayoutsSummaryQuery();
  const {
    data: upcomingPayoutsSummaryData,
    isSuccess: upcomingPayoutsSummarySuccess,
    isFetching: fetchingUpcomingPayoutsSummary
  } = getUpcomingPayoutsSummaryState;

  const { inFutureAmount, keyLabelMapping, payoutSendingVia, nextPayout } = upcomingPayoutsSummaryData ?? {};
  const { bankingEnabled, paymentSystem } = useAppSelector(selectPaymentSystemConfig);
  const payoutPermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const { bankName, accountNumber } = currentTeamBank;

  useEffect(() => {
    if (currentTeam.id && paymentSystem) {
      setHasPermissionsToView(
        checkPermissions({
          paymentSystem,
          viewPayoutsBySelf: payoutPermissions.viewPayoutsBySelf,
          viewPayoutsByTeam: payoutPermissions.viewPayoutsByTeam
        })
      );
    }
  }, [currentTeam.id, paymentSystem]);

  useEffect(() => {
    if (currentTeam.id && hasPermissionsToView) {
      getUpcomingPayoutsSummaryQuery({
        teamId: currentTeam.id,
        timezone: DateUtils.getTimezoneLongName()
      });
    }
  }, [currentTeam.id, hasPermissionsToView]);

  useEffect(() => {
    setHasPermissionsToView(
      checkPermissions({
        paymentSystem,
        viewPayoutsBySelf: payoutPermissions.viewPayoutsBySelf,
        viewPayoutsByTeam: payoutPermissions.viewPayoutsByTeam
      })
    );
  }, [payoutPermissions.viewPayoutsBySelf, payoutPermissions.viewPayoutsByTeam]);

  const viewAllTransactions = () => {
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Collected & Refunds filter applied',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    logAnalyticEvent(PayoutsCleverTapEvents.webUpcomingPayoutsViewAllTransactionsClick);
    navigate(`/payouts/balance/${currentTeam.id}/upcoming-payouts/${PayoutRootPage.BALANCE}/transactions`);
  };

  const handleNextPayoutFooterClick = (ctaType: string, ctaAction: string) => {
    if (ctaType === CTA_TYPES.URL || ctaType === 'EXTERNAL_URL') {
      openInNewTab(ctaAction);
      return;
    }
    if (ctaType === CTA_TYPES.DEEPLINK) {
      navigate(ctaAction);
    }
  };

  const handleBannerCtaClick = () => {
    const cta = nextPayout?.banner?.cta;
    const webCtaType = cta?.webCtaType;
    const webCtaAction = cta?.webCtaAction;
    if (!webCtaType || !webCtaAction) return;
    handleNextPayoutFooterClick(webCtaType, webCtaAction);
  };

  const renderNextPayoutBanner = () => {
    const banner = nextPayout?.banner;
    const body = banner?.body?.trim();
    if (!body) return null;

    const cta = banner?.cta;
    const ctaLabel = cta?.label?.trim();
    const hasWebCta = Boolean(ctaLabel && cta?.webCtaType && cta?.webCtaAction);

    return (
      <div className="flex px-[30px]">
        <div
          className={
            'mb-2.5 flex w-full flex-col gap-3 rounded-md border border-pending-orange bg-[#F7E2C0]/20 ' +
            'py-4 pl-3 pr-5'
          }>
          <div className="flex gap-3">
            <OrangeInfoIcon className="shrink-0" />
            <div className="text-sbase font-normal text-primaryText">{body}</div>
          </div>
          {hasWebCta ? (
            <CustomButton
              id="upcoming-payouts-next-payout-banner-cta"
              onClick={handleBannerCtaClick}
              className="h-10 w-full shrink-0 text-sbase font-semibold text-white"
              EndIcon={WhiteRightArrowSmall}>
              {ctaLabel}
            </CustomButton>
          ) : null}
        </div>
      </div>
    );
  };

  const renderTotalField = () => {
    const { label, value } = keyLabelMapping?.find(item => item.isFinal) ?? {};
    if (!label || !value) return null;
    return (
      <div className="flex items-center justify-between">
        <div className="text-17px font-bold text-primaryText">{label}</div>
        <div className="text-17px font-bold text-tertiaryText">{getAmountWithCurrency(+value)}</div>
      </div>
    );
  };

  const onLearnMoreClick = () => {
    openScanpayFAQInNewTab(PayoutsCleverTapEvents.webUpcomingPayoutsDetailsLearnMoreClick);
  };

  return (
    <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
      <div className="flex h-full w-96 flex-col">
        <If condition={fetchingUpcomingPayoutsSummary && hasPermissionsToView}>
          <div className="flex h-full w-96 items-center justify-center">
            <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
          </div>
        </If>
        <If condition={(upcomingPayoutsSummarySuccess && !fetchingUpcomingPayoutsSummary) || !hasPermissionsToView}>
          <div className="flex h-full w-full flex-col gap-5 pt-5">
            <div className="flex w-full items-center gap-2 pl-[14px]">
              <div onClick={handleSidePanelClose} className="cursor-pointer">
                <NavCloseIcon id="upcoming-payouts-details-panel-close" />
              </div>
              <div className="text-17px font-semibold text-heading">Upcoming payouts</div>
            </div>
            {hasPermissionsToView ? (
              <div className="customNormalScroll h-full overflow-auto">
                <div
                  className="flex cursor-pointer items-center justify-center bg-secondaryBtn py-2"
                  role="presentation"
                  onClick={onLearnMoreClick}>
                  <RedirectIcon className="shrink-0 pr-2" />
                  <div className="text-sbase font-semibold text-primaryBtn">
                    Learn about getting paid through ScanPay
                  </div>
                </div>
                <div className="flex flex-col gap-5 py-[30px]">
                  <div className="flex flex-col gap-2.5 text-center">
                    <div className="text-sbase font-normal text-accentText">Estimated to be paid out</div>
                    <div className="text-3xl font-bold text-tertiaryText">{getAmountWithCurrency(+inFutureAmount)}</div>
                    {bankingEnabled && (
                      <div className="text-sbase font-normal text-accentText">
                        To {bankName} {accountNumber}
                      </div>
                    )}
                  </div>
                  {renderNextPayoutBanner()}
                </div>
                <Divider className="w-full border-borderGray" />
                <div className="mt-5 flex flex-col gap-5">
                  {nextPayout ? (
                    <>
                      <PayoutDistributionTimeline nextPayout={nextPayout} onFooterClick={handleNextPayoutFooterClick} />
                      <Divider className="w-full border-borderGray" />
                    </>
                  ) : null}
                  <div className="flex flex-col px-[30px]">
                    <div className="text-sbase font-normal text-accentText">Amount breakdown</div>
                    <div className="mt-5 flex flex-col gap-[15px]">
                      {keyLabelMapping?.map(amountBreakdownItem => {
                        if (
                          (amountBreakdownItem.doNotShowWhenZero && +amountBreakdownItem.value === 0) ||
                          amountBreakdownItem.isFinal
                        ) {
                          return null;
                        }
                        return (
                          <div key={amountBreakdownItem.key} className="flex items-center justify-between">
                            <div className="text-17px font-semibold text-primaryText">{amountBreakdownItem.label}</div>
                            <div className="text-17px font-semibold text-tertiaryText">
                              {getAmountWithCurrency(+amountBreakdownItem.value)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Divider className="my-5 w-full border-borderGray" />
                    {renderTotalField()}
                  </div>
                  {bankingEnabled && (
                    <>
                      <Divider className="w-full border-borderGray" />
                      <div className="flex flex-col px-[30px]">
                        <div className="text-sbase font-normal text-accentText">Overview</div>
                        <div className="mt-5 flex flex-col gap-[15px]">
                          <div className="flex items-start justify-between">
                            <div className="text-17px font-normal text-primaryText">Sending to</div>
                            <div className="text-right text-17px font-normal text-tertiaryText">
                              {bankName && accountNumber ? (
                                <>
                                  <div>{bankName}</div>
                                  <div>{accountNumber}</div>
                                </>
                              ) : (
                                <div>-</div>
                              )}
                            </div>
                          </div>
                          {payoutSendingVia && (
                            <div className="flex items-center justify-between">
                              <div className="text-17px font-normal text-primaryText">Sending via</div>
                              <div className="text-17px font-normal text-tertiaryText">{payoutSendingVia}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  <Divider className="w-full border-borderGray" />
                  {paymentSystem !== PaymentSystem.LIP && (
                    <div
                      className="mx-[30px] mb-5 flex h-12 cursor-pointer items-center justify-center rounded-md
                      border border-gray-1 text-17px font-normal text-primaryBtn"
                      role="presentation"
                      onClick={viewAllTransactions}>
                      View all transactions
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <NoPermissions />
            )}
          </div>
        </If>
      </div>
    </SidePanel>
  );
};

export default UpComingPayoutDetailsPanel;
